<template>
  <v-card class="d-flex flex-column">
    <v-card-item
      class="pa-0 overflow-y-auto flex-grow-1"
      style="max-height: 500px"
    >
      <v-treeview
        density="compact"
        indent-lines
        item-children="children"
        item-value="id"
        :items="treeItems"
        items-registration="props"
        open-on-click
      >
        <template #prepend="{ item }">
          <v-icon
            v-if="item.type === 'folder'"
            icon="far fa-folder"
            size="20"
          />
          <v-icon v-else-if="item.type === 'file'" class="opacity-100">
            <NodeIcon
              :mime-type="getFileIconNode(item)?.mimeType"
              :name="getFileIconNode(item)?.name"
              :type="getFileIconNode(item)?.type"
            />
          </v-icon>
        </template>

        <template #title="{ item }">
          <span>
            {{ item.title }}
          </span>
          <span class="ml-2">
            <span
              v-if="item.fileCount !== undefined"
              class="text-body-small text-grey"
            >
              {{ t('hints.n_folders', item.children?.length ?? 0) }} &
              {{ t('hints.n_files', item.fileCount) }}
            </span>
          </span>
        </template>

        <template #append="{ item }">
          <v-icon
            v-if="item.status === 'processing' || item.status === 'added'"
            v-tooltip="
              t('upload.status.' + item.status) + ` (${item.progress} %)`
            "
            class="mr-1"
          >
            <v-progress-circular :model-value="item.progress" width="2" />
          </v-icon>
          <v-icon
            v-else
            class="mr-1"
            v-bind="getTaskUploadStateIcon(item.status)"
          ></v-icon>
        </template>
      </v-treeview>
    </v-card-item>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import NodeIcon from '@/components/node/NodeIcon.vue'
import { useTaskUpload } from '@/components/upload'
import { TaskUpload, TaskUploadNode } from '../types'

type TreeNode = Pick<TaskUploadNode, 'status' | 'progress' | 'data'> & {
  id: string
  title: string
  type: 'folder' | 'file'
  fileCount?: number
  children?: TreeNode[]
}

const props = defineProps({
  taskUpload: {
    type: Object as PropType<TaskUpload>,
    default: () => ({}),
  },
})

const { getTaskUploadStateIcon } = useTaskUpload()
const { t } = useI18n()

function buildFileNode(file: TaskUploadNode): TreeNode {
  return {
    id: file.id,
    title: file.data?.file?.name ?? '',
    type: 'file',
    status: file.status,
    progress: file.progress,
    data: file.data,
  }
}

const treeItems = computed<TreeNode[]>(() => {
  const folderMap = new Map<
    string,
    { folder: TaskUploadNode; files: TaskUploadNode[] }
  >()

  for (const [folderId, folder] of Object.entries(
    props.taskUpload.folders ?? {},
  )) {
    folderMap.set(folderId, { folder, files: [] })
  }

  const rootFiles: TaskUploadNode[] = []

  for (const file of props.taskUpload.files ?? []) {
    const fid = file.data?.idFolder
    if (fid && folderMap.has(fid)) {
      folderMap.get(fid)!.files.push(file)
    } else {
      rootFiles.push(file)
    }
  }

  // Build TreeNode per folder (without children yet)
  const nodeMap = new Map<string, TreeNode>()

  for (const [folderId, { folder, files }] of folderMap.entries()) {
    nodeMap.set(folderId, {
      id: folderId,
      title: folder.data?.name ?? folderId,
      type: 'folder',
      status: folder.status,
      data: folder.data,
      progress: folder.progress,
      fileCount: files.length,
      children: files.map((f) => buildFileNode(f)),
      // ...(files.length ? { children: files.map((f) => buildFileNode(f)) } : {}),
    })
  }

  // Wire up folder children into their parents
  const roots: TreeNode[] = []

  for (const [folderId, { folder }] of folderMap.entries()) {
    const parentId = folder.data?.parent
    const hasRealParent = !!parentId && folderMap.has(parentId)

    if (hasRealParent) {
      // Append this folder node into its parent's children
      nodeMap.get(parentId!)!.children?.push(nodeMap.get(folderId)!)
    } else {
      roots.push(nodeMap.get(folderId)!)
    }
  }

  rootFiles.forEach((f) => roots.push(buildFileNode(f)))

  return roots
})

function getFileIconNode(taskUploadNode: TaskUploadNode) {
  if (!taskUploadNode) return undefined

  if (taskUploadNode.status === 'completed') {
    return taskUploadNode.data?.node
  } else {
    return undefined
  }
}
</script>
